import dayjs from 'dayjs';
import { action, computed, makeObservable, observable, ObservableSet, reaction } from 'mobx';
import { useEffect } from 'react';
import { API } from '~/api';
import { UserCountrySource } from '~/shared/dto/model.enums';
import { Async } from '~/shared/tools/async';
import { Singleton } from '../shared/service/service.base';
import { ApiService } from './service.api';

export type DashboardPeriod = 1 | 7 | 30;
export type DashboardActivityValue =
	| 'LIKE'
	| 'UNLIKE'
	| 'FOLLOW'
	| 'UNFOLLOW'
	| 'GRANTED'
	| 'DENIED'
	| 'BLOCK'
	| 'CONTACT';

export type DashboardActivity = {
	key: string;
	type: API.UserActivityType;
	action?: API.UserActivityAction;
	value?: DashboardActivityValue;
	count: number;
	activities?: Exclude<DashboardActivity, 'activities'>[];
};

type DashboardField<T> = {
	key: keyof T;
	percent?: boolean;
	invert?: boolean;
	getValue?: (data: T) => number;
	getDesc?: (data: T) => string;
};

@Singleton()
export class DashboardService {
	readonly asyncPeriod = new Async();
	readonly asyncPeriodCountries = new Async();
	readonly asyncPeriodActivities = new Async();

	readonly asyncGeneral = new Async();
	readonly asyncCountries = new Async();

	private generalBaseFields: DashboardField<API.AnalyticsGeneralBase>[] = [
		{ key: 'brands' },
		{ key: 'fragrances' },
		{ key: 'perfumers' },
	];

	private generalUserFields: DashboardField<API.AnalyticsGeneralUsers>[] = [
		{ key: 'users' },
		{ key: 'locationGranted' },
		{ key: 'stickinessRatio', percent: true },
		{ key: 'pushGranted' },
	];

	private generalUserUGCFields: DashboardField<API.AnalyticsUGC>[] = [
		{ key: 'fragrancesWithExperience' },
		{ key: 'reviews' },
		{ key: 'fragrancesWithReview' },
		{ key: 'posts' },
		{ key: 'postsWithText' },
		{ key: 'postComments' },
		{ key: 'layerings' },
		{ key: 'fragrancesWithLayering' },
		{ key: 'fragrancesWithNote' },
		{ key: 'products' },
		{ key: 'fragrancesWithProduct' },
		{ key: 'referrals' },
		{ key: 'mentions' },
	];

	private fields: DashboardField<API.AnalyticsByPeriod>[] = [
		{ key: 'actionUsers' },
		{ key: 'returningUsers' },
		{ key: 'newUsers' },
		{ key: 'deletedUsers' },
		{ key: 'actionAverage' },
		{
			key: 'churn',
			percent: true,
			invert: true,
			getValue: data => data.churn.total && Math.round((data.churn.count / data.churn.total) * 100),
			getDesc: data => `(${data.churn.count}/${data.churn.total})`,
		},
		{
			key: 'recovery',
			percent: true,
			getValue: data => data.recovery.total && Math.round((data.recovery.count / data.recovery.total) * 100),
			getDesc: data => `(${data.recovery.count}/${data.recovery.total})`,
		},
		{
			key: 'retention',
			percent: true,
			getValue: data => data.retention.total && Math.round((data.retention.count / data.retention.total) * 100),
			getDesc: data => `(${data.retention.count}/${data.retention.total})`,
		},
	];

	@observable period: DashboardPeriod = 1;

	@observable.ref periodAnalytics?: API.AnalyticsPeriods = undefined;
	@observable.ref periodCountries?: API.AnalyticsCountries = undefined;
	@observable.ref periodActivities?: API.AnalyticsActivities = undefined;

	@observable.ref general?: API.AnalyticsGeneral = undefined;
	@observable.ref countries?: API.AnalyticsCountries = undefined;
	@observable.ref ugc?: API.AnalyticsUGC = undefined;

	openActions: ObservableSet<string> = new ObservableSet();

	@observable countriesSource: UserCountrySource | undefined = undefined;

	constructor(private api: ApiService) {
		makeObservable(this);
	}

	usePeriodAnalytics = () => {
		useEffect(() => {
			this.reloadPeriod();
			this.subscribe(this.reloadPeriod);
		}, []);

		useEffect(() => {
			this.reloadPeriodCountries();
			this.subscribe(this.reloadPeriodCountries);
		}, []);
	};

	reloadPeriod = () => {
		this.asyncPeriod.call(async () => {
			const { data } = await this.api.analyticsPeriod({ days: this.period });
			return () => {
				this.periodAnalytics = data;
			};
		});
	};

	reloadPeriodCountries = () => {
		this.asyncPeriodCountries.call(async () => {
			const { data } = await this.api.analyticsPeriodCountries({
				source: this.countriesSource,
				days: this.period,
			});
			return () => {
				this.periodCountries = data;
			};
		});
	};

	useActivities = () => {
		useEffect(() => this.reloadActivities(), []);
		useEffect(() => this.subscribe(this.reloadActivities), []);
	};

	useGeneral = () => {
		useEffect(() => this.reloadGeneral(), []);
		useEffect(() => this.reloadCountries(), []);
	};

	reloadActivities = () => {
		this.asyncPeriodActivities.call(async () => {
			const { data } = await this.api.analyticsPeriodActivities({ days: this.period });
			return () => {
				this.periodActivities = data;
			};
		});
	};

	reloadGeneral = () => {
		this.asyncGeneral.call(async () => {
			const [general, ugc] = await Promise.all([this.api.analyticsGeneral(), this.api.analyticsUgc()]);
			return () => {
				this.general = general.data;
				this.ugc = ugc.data;
			};
		});
	};

	reloadCountries = () => {
		this.asyncCountries.call(async () => {
			const countries = await this.api.analyticsCountries({ source: this.countriesSource });
			return () => {
				this.countries = countries.data;
			};
		});
	};

	@action.bound setCountriesSource(source: UserCountrySource | undefined) {
		this.countriesSource = source;
		this.reloadCountries();
	}

	@action.bound toggleAction(key: string) {
		if (this.openActions.has(key)) this.openActions.delete(key);
		else this.openActions.add(key);
	}

	@action.bound setPeriod(value: DashboardPeriod) {
		this.period = value;
	}

	subscribe(cb: () => void): void | (() => void) {
		return reaction(() => [this.period], cb);
	}

	@computed
	get generalBaseData() {
		const base = this.general?.base;
		if (!base) return [];
		return this.generalBaseFields.map(f => ({ ...f, value: base[f.key] }));
	}

	@computed
	get generalUsersData() {
		const users = this.general?.users;
		if (!users) return [];
		return this.generalUserFields.map(f => ({ ...f, value: users[f.key] }));
	}

	@computed
	get generalCountriesData() {
		const countries = this.countries;
		if (!countries) return [];
		return countries.items;
	}

	@computed
	get generalUGCData() {
		const data = this.ugc;
		if (!data) return [];
		return this.generalUserUGCFields.map(f => ({ ...f, value: data[f.key] }));
	}

	@computed
	get periodData() {
		if (!this.periodAnalytics) return [];
		const { current, previous } = this.periodAnalytics;
		return this.fields.map(f => ({
			...f,
			value: f.getValue ? f.getValue(current) : Number(current[f.key]),
			prev: f.getValue ? f.getValue(previous) : Number(previous[f.key]),
			desc: f.getDesc ? f.getDesc(current) : undefined,
		}));
	}

	@computed
	get periodCountriesList() {
		if (!this.periodCountries) return [];
		return this.periodCountries.items;
	}

	@computed
	get periodActivitiesList(): {
		primary: DashboardActivity[];
		secondary: DashboardActivity[];
	} {
		if (!this.periodActivities) return { primary: [], secondary: [] };

		const all = this.periodActivities.items.map(a => ({
			...a,
			key: this.getActivityKey(a.type, a.action, a.value),
			value: a.value?.toUpperCase() as DashboardActivityValue,
		}));

		return {
			primary: this.getPeriodActions(all.filter(this.isPrimaryActivity)),
			secondary: this.getPeriodActions(all.filter(this.isSecondaryActivity)),
		};
	}

	@computed
	get uniqueTypes() {
		if (!this.periodActivities) return [];
		return Array.from(new Set(this.periodActivities.items.map(a => a.type)));
	}

	get uniqueActions() {
		return ['CREATE', 'UPDATE', 'DELETE', 'GET'] as API.UserActivityAction[];
	}

	get uniqueValues() {
		return ['LIKE', 'UNLIKE', 'FOLLOW', 'UNFOLLOW', 'GRANTED', 'DENIED', 'BLOCK', 'CONTACT'];
	}

	getPeriodActions(values: DashboardActivity[]): DashboardActivity[] {
		if (values.length === 0) return [];
		const result: DashboardActivity[] = [];

		this.uniqueTypes.forEach(type => {
			const items = values
				.filter(a => a.type === type)
				.reduce((acc, curr) => {
					if ((curr.action === 'CREATE' || curr.action === 'UPDATE') && curr.value) {
						const item = {
							...curr,
							action: undefined,
							key: this.getActivityKey(curr.type, undefined, curr.value),
						};
						if (acc.some(a => a.key === item.key)) {
							const idx = acc.findIndex(a => a.key === item.key);
							acc[idx].count += item.count;
							return acc;
						} else acc.push(item);
					} else acc.push(curr);
					return acc;
				}, [] as DashboardActivity[]);

			if (items.length === 0) return;

			result.push({
				type,
				count: items.reduce((sum, curr) => sum + curr.count, 0),
				activities: items.sort((a, b) => a.key.localeCompare(b.key)),
				key: this.getActivityKey(type, undefined, undefined),
			});
		});

		return result.sort((a, b) => a.key.localeCompare(b.key));
	}

	@computed
	get periodDates() {
		const today = dayjs();
		const format = 'MMM DD, YYYY';
		switch (this.period) {
			case 1:
				return { from: today.clone().subtract(1, 'day').format(format), to: today.format(format) };
			case 7:
				return { from: today.clone().subtract(7, 'day').format(format), to: today.format(format) };
			case 30:
				return { from: today.clone().subtract(30, 'day').format(format), to: today.format(format) };
			default:
				return null;
		}
	}

	private getActivityKey = (type: API.UserActivityType, action: string | undefined, value: string | undefined) => {
		return `${type}${action ? `_${action}` : ''}${value ? `_${value.toUpperCase()}` : ''}`;
	};

	private secondaryActivities: { type: API.UserActivityType; action?: API.UserActivityAction }[] = [
		{ type: 'POSTREACTION' },
		{ type: 'POSTCOMMENTREACTION' },
		{ type: 'USERRELATION' },
		{ type: 'USER', action: 'GET' },
		{ type: 'SHELF', action: 'GET' },
		{ type: 'FRAGRANCE', action: 'GET' },
	];

	private isSecondaryActivity = (activity: API.AnalyticsActivity): boolean => {
		return this.secondaryActivities.some(
			s => s.type === activity.type && (s.action ? s.action === activity.action : true),
		);
	};

	private isPrimaryActivity = (activity: API.AnalyticsActivity): boolean => {
		return this.isSecondaryActivity(activity) === false;
	};
}
